import type { StyleSpecification } from "maplibre-gl";

import type { Basemap } from "./basemaps";

/**
 * Builds a minimal MapLibre style for a base map. A base map with several tile
 * templates (e.g. imagery + labels) becomes one raster source and one raster
 * layer per template, drawn in array order so overlays sit on top.
 *
 * No `glyphs`/`sprite`: stage 2 has no symbol layers, and MapLibre rejects a
 * style whose glyphs key is present but empty. Data layers get added on the
 * live map object in later stages, not baked into this style.
 */
export function buildStyle(basemap: Basemap): StyleSpecification {
  const sources: StyleSpecification["sources"] = {};
  const layers: StyleSpecification["layers"] = [];

  basemap.tiles.forEach((template, index) => {
    const sourceId = `basemap-${index}`;
    sources[sourceId] = {
      type: "raster",
      tiles: [template],
      tileSize: 256,
      attribution: basemap.attribution,
      maxzoom: basemap.maxZoom,
    };
    layers.push({
      id: sourceId,
      type: "raster",
      source: sourceId,
      ...(basemap.rasterPaint ? { paint: basemap.rasterPaint } : {}),
    });
  });

  return {
    version: 8,
    // Public OpenMapTiles font endpoint so symbol layers (region and source
    // labels) have glyphs. If it's unreachable, labels simply don't draw —
    // nothing else breaks.
    glyphs: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
    sources,
    layers,
  };
}
